<template>
    <div ref="kCharts" class="k-charts"></div>
</template>

<script>
    import * as echarts from "echarts";
    import dayjs from "dayjs";

    // import Thread from "../../js/thread.js";
    import AnyFormat from "../../lib/any-format.js";
    
    export default {
        name: "ModalPosition",
        props: {
            product: {
                type: Object,
                default() {
                    return null;
                }
            },
            paintIndex: {
                type: Boolean,
                default: true
            },
            paintFund: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                chart: null,
                indexTimes: [],
                fundTimes: [],
                tradeDays: []
            };
        },
        computed: {
            productIndexId() {
                return this.product?.index?.id;
            },
            productFundId() {
                return this.product?.fund?.id;
            },
            willPaintIndex() {
                return this.paintIndex && this.product?.index?.id;
            },
            willPaintFund() {
                return this.paintFund && this.product?.fund?.id;
            },
            indexTimesMap() {
                let map = new Map();
                for (let i=0; i<this.indexTimes.length; i++) {
                    let indexTime = this.indexTimes[i];
                    let time = indexTime.time;
                    let timeStr = AnyFormat.format(time, "YYYY-MM-DD");
                    map.set(timeStr, indexTime);
                }
                return map;
            },
            fundTimesMap() {
                let map = new Map();
                for (let i=0; i<this.fundTimes.length; i++) {
                    let fundTime = this.fundTimes[i];
                    let time = fundTime.time;
                    let timeStr = AnyFormat.format(time, "YYYY-MM-DD");
                    map.set(timeStr, fundTime);
                }
                return map;
            }
        },
        watch: {
            paintIndex() {
                this.repaint();
            },
            paintFund() {
                this.repaint();
            },
            productIndexId() {
                this.init();
            },
            productFundId() {
                this.init();
            }
        },
        mounted() {
            this.init();
        },
        beforeDestroy() {
            this.destroy();
        },
        methods: {
            getPositionByDate(date) {
                let activePositionRecord = null;
                for (let i=0; i<this.product.positionRecords.length; i++) {
                    if (this.product.positionRecords[i].date.isAfter(date, "day")) {
                        break;
                    }
                    activePositionRecord = this.product.positionRecords[i];
                }
                if (activePositionRecord) {
                    return activePositionRecord.positionPost;
                }
                if (this.product.positionRecords.length > 0) {
                    return this.product.positionRecords[0].positionPost - this.product.positionRecords[0].position;
                }
                return this.product.position;
            },
            async init() {
                this.tradeDays = await this.$remotes.server.financing.tradeDay.list();
                if (this.product?.index?.id) {
                    this.indexTimes = await this.$remotes.server.financing.index.listIndexTime(this.product.index.id);
                }
                if (this.product?.fund?.id) {
                    this.fundTimes = await this.$remotes.server.financing.index.listFundTime(this.product.fund.id);
                }
                this.repaint();
            },
            async repaint() {
                this.destroy();
                
                // 转换数据
                let times = [];
                let candlestick = [];
                let fundValues = [];
                let positions = [];
                for (let i=this.tradeDays.length-1; i>=0; i--) {
                    let tradeDay = this.tradeDays[i];
                    let date = tradeDay.date;
                    if (date.isSame(dayjs(), "d")) {
                        continue;
                    }
                    let timeStr = AnyFormat.format(date, "YYYY-MM-DD");
                    
                    times.push(timeStr);
                    if (this.willPaintIndex) {
                        let indexTime = this.indexTimesMap.get(timeStr);
                        candlestick.push([
                            indexTime.opening,
                            indexTime.closing,
                            indexTime.lowest,
                            indexTime.highest
                        ]);
                    }
                    if (this.willPaintFund) {
                        let fundTime = this.fundTimesMap.get(timeStr);
                        fundValues.push(fundTime.accumulationValue);
                    }
                    positions.push(this.getPositionByDate(date));
                }
                
                // 基于准备好的dom，初始化echarts实例
                let chart = echarts.init(this.$refs.kCharts);
                
                // 生成y轴，系列
                let yAxis = [];
                let series = [];
                let seriesIndex = 0;
                // 指数轴
                if (this.willPaintIndex) {
                    yAxis.push({
                        name: this.product.index.name,
                        type: "value",
                        scale: true
                    });
                    series.push({
                        type: "candlestick",
                        data: candlestick
                    });
                    seriesIndex++;
                }
                // 基金轴
                if (this.willPaintFund) {
                    yAxis.push({
                        name: this.product.fund.name,
                        type: "value",
                        scale: true,
                        show: !this.willPaintIndex
                    });
                    series.push({
                        type: "line",
                        data: fundValues,
                        yAxisIndex: seriesIndex,
                        itemStyle: {
                            normal: {
                                color: '#ff9f17', //改变折线点的颜色
                                lineStyle: {
                                    color: '#ff9f17' //改变折线颜色
                                }
                            }
                        }
                    });
                    seriesIndex++;
                }
                // 仓位轴
                yAxis.push({
                    name: "仓位/%",
                    type: "value",
                    min: 0,
                    max: (value) => Math.max(value.max, 100),
                    splitLine: false
                });
                series.push({
                    type: "line",
                    data: positions,
                    yAxisIndex: seriesIndex,
                    itemStyle: {
                        normal: {
                            color: '#1079ff', //改变折线点的颜色
                            lineStyle: {
                                color: '#1079ff' //改变折线颜色
                            }
                        }
                    }
                });
                seriesIndex++;
                
                // 指定图表的配置项和数据
                let option = {
                    xAxis: {
                        data: times,
                        axisLabel: {  
                           rotate: 40,
                           showMaxLabel: true
                        }
                    },
                    yAxis: yAxis,
                    series: series,
                    grid: {  
                        left: "15%",  
                        bottom: "30%"  
                    }
                };
                
                // 使用刚指定的配置项和数据显示图表。
                chart.setOption(option);
                window.onresize = () => {
                    chart.resize();
                };
                
                this.chart = chart;
            },
            destroy() {
                if (this.chart) {
                    this.chart.dispose();
                    this.chart = null;
                }
            }
        }
    }
    
</script>

<style scoped lang="scss">
    .k-charts {
        height: 260px;
    }
</style>
